import React from 'react';
import { Alert, Button, Select, Space, Spin, Tag, Typography } from 'antd';

import { SyntheticBondGenerationDraft, SyntheticBondSeries } from 'state';

type Props = {
  canSave?: boolean;
  disabled?: boolean;
  draft: SyntheticBondGenerationDraft;
  error?: string;
  fetching?: boolean;
  saving?: boolean;
  saveError?: string;
  series?: SyntheticBondSeries[];
  onSelect: (series?: SyntheticBondSeries) => void;
  onSave: () => void;
};

const SeriesSelector: React.FC<Props> = ({
  disabled,
  canSave,
  draft,
  error,
  fetching,
  onSave,
  onSelect,
  saveError,
  saving,
  series = [],
}) => {
  const selected = series.find(item => item.id === draft.series_id);

  return (
    <section className="synthetic-bonds__series-selector">
      <div className="synthetic-bonds__section-title">
        <div>
          <Typography.Title level={4}>1. Choose a Series</Typography.Title>
          <Typography.Paragraph type="secondary">
            Series identity is country plus initial tenor. Selecting one loads
            its maintained currency, reference curve, and generation defaults.
          </Typography.Paragraph>
        </div>
      </div>

      <Space align="start" size="middle" wrap>
        <Select<string>
          allowClear
          disabled={disabled || fetching}
          loading={fetching}
          options={series.map(item => ({
            value: item.id,
            disabled: !item.is_active,
            label: `${item.code} · ${item.currency.symbol} · ${
              item.reference_curve_preset.code
            } curve · ${item.member_count} issues${
              item.earliest_issue_date && item.latest_issue_date
                ? ` · ${item.earliest_issue_date} → ${item.latest_issue_date}`
                : ''
            }${item.is_active ? '' : ' · inactive'}`,
          }))}
          optionFilterProp="label"
          placeholder="Select an existing Series"
          showSearch
          style={{ minWidth: 360 }}
          value={draft.series_id ?? undefined}
          onChange={value => onSelect(series.find(item => item.id === value))}
          onClear={() => onSelect(undefined)}
        />
        {selected ? (
          <>
            <Tag color="blue">{selected.code}</Tag>
            <Tag>{selected.member_count} existing issues</Tag>
            {selected.latest_issue_date && (
              <Tag>latest {selected.latest_issue_date}</Tag>
            )}
            <Button disabled={disabled} onClick={() => onSelect(undefined)}>
              Define a new Series
            </Button>
          </>
        ) : fetching ? (
          <Spin size="small" />
        ) : null}
      </Space>

      {error && (
        <Alert
          className="synthetic-bonds__request-error"
          description={error}
          message="Series could not be loaded"
          showIcon
          type="error"
        />
      )}

      {selected ? (
        <Alert
          className="synthetic-bonds__request-error"
          description="The Series fields below are locked for this pass. Define a new Series if the country/tenor identity does not exist; update the Series separately if its maintained defaults need to change."
          message={`${selected.code} defaults loaded`}
          showIcon
          type="success"
        />
      ) : (
        <Alert
          action={
            <Button
              disabled={disabled || !canSave}
              loading={saving}
              onClick={onSave}
              type="primary"
            >
              Save Series
            </Button>
          }
          className="synthetic-bonds__request-error"
          description={
            saveError ||
            'Complete one country code, one initial tenor, currency, reference family, and metadata below, then save the Series before previewing bonds.'
          }
          message="New Series definition"
          showIcon
          type={saveError ? 'error' : 'info'}
        />
      )}
    </section>
  );
};

export default SeriesSelector;
